import axios from 'axios';

// Auth token is set globally by AuthContext via axios.defaults.headers.common['Authorization']
// All calls below automatically include it when the user is logged in.

// ---------------------------------------------------------------------------
// Public endpoints (no auth required)
// ---------------------------------------------------------------------------

export const getFacilities = (params) =>
  axios.get('/api/facilities', { params }).then((r) => r.data);

export const getFullFacilities = (params) =>
  axios.get('/api/facilities/full', { params }).then((r) => r.data);

export const getFacility = (id) =>
  axios.get(`/api/facilities/${id}`).then((r) => r.data);

export const getCounties = () =>
  axios.get('/api/counties').then((r) => r.data);

export const getLastSync = () =>
  axios.get('/api/facilities/meta/last-sync').then((r) => r.data);

// ---------------------------------------------------------------------------
// Auth endpoints
// ---------------------------------------------------------------------------

export const signup = (data) =>
  axios.post('/api/auth/signup', data).then((r) => r.data);

export const login = (data) =>
  axios.post('/api/auth/login', data).then((r) => r.data);

export const trialSignup = (data) =>
  axios.post('/api/auth/trial', data).then((r) => r.data);

// ---------------------------------------------------------------------------
// Account (authenticated)
// ---------------------------------------------------------------------------

export const getAccount = () =>
  axios.get('/api/account').then((r) => r.data);

// ---------------------------------------------------------------------------
// Subscriptions (authenticated)
// ---------------------------------------------------------------------------

export const createCheckoutSession = (data) =>
  axios.post('/api/subscribe', data).then((r) => r.data);

export const cancelSubscription = () =>
  axios.delete('/api/subscribe').then((r) => r.data);

// ---------------------------------------------------------------------------
// Favorites (authenticated)
// ---------------------------------------------------------------------------

export const getFavorites = () =>
  axios.get('/api/favorites').then((r) => r.data);

export const addFavorite = (facilityId) =>
  axios.post(`/api/favorites/${facilityId}`).then((r) => r.data);

export const removeFavorite = (facilityId) =>
  axios.delete(`/api/favorites/${facilityId}`).then((r) => r.data);

// ---------------------------------------------------------------------------
// Notes (authenticated)
// ---------------------------------------------------------------------------

export const getNotes = (facilityId) =>
  axios.get(`/api/notes/${facilityId}`).then((r) => r.data);

export const addNote = (facilityId, data) =>
  axios.post(`/api/notes/${facilityId}`, data).then((r) => r.data);

export const updateNote = (noteId, data) =>
  axios.put(`/api/notes/${noteId}`, data).then((r) => r.data);

export const deleteNote = (noteId) =>
  axios.delete(`/api/notes/${noteId}`).then((r) => r.data);

// ---------------------------------------------------------------------------
// Admin endpoints
// ---------------------------------------------------------------------------

export const uploadExcel = (formData) =>
  axios.post('/api/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((r) => r.data);

export const getAdminUsers = (params) =>
  axios.get('/api/admin/users', { params }).then((r) => r.data);

export const createCompUser = (data) =>
  axios.post('/api/admin/users', data).then((r) => r.data);

export const updateAdminUser = (id, data) =>
  axios.put(`/api/admin/users/${id}`, data).then((r) => r.data);

export const revokeAdminUser = (id) =>
  axios.delete(`/api/admin/users/${id}`).then((r) => r.data);

export const getPromoCodes = () =>
  axios.get('/api/admin/promo').then((r) => r.data);

export const createPromoCode = (data) =>
  axios.post('/api/admin/promo', data).then((r) => r.data);

export const updatePromoCode = (id, data) =>
  axios.put(`/api/admin/promo/${id}`, data).then((r) => r.data);

export const deletePromoCode = (id) =>
  axios.delete(`/api/admin/promo/${id}`).then((r) => r.data);

export const getAdminStats = () =>
  axios.get('/api/admin/stats').then((r) => r.data);

export const getAdminNotes = () =>
  axios.get('/api/admin/notes').then((r) => r.data);

export const getManualFacilities = () =>
  axios.get('/api/admin/facilities').then((r) => r.data);

export const createManualFacility = (data) =>
  axios.post('/api/admin/facilities', data).then((r) => r.data);

export const deleteManualFacility = (id) =>
  axios.delete(`/api/admin/facilities/${id}`).then((r) => r.data);

export const editFacility = (id, data) =>
  axios.patch(`/api/admin/facilities/${id}`, data).then((r) => r.data);

// ---------------------------------------------------------------------------
// My Facility (linked org user)
// ---------------------------------------------------------------------------

export const requestFacilityLink = (facilityId) =>
  axios.post('/api/my-facility/link', { facility_id: facilityId }).then((r) => r.data);

export const getMyFacility = (facilityId) =>
  axios.get('/api/my-facility', { params: facilityId ? { facility_id: facilityId } : undefined }).then((r) => r.data);

export const updateMyFacility = (data) =>
  axios.patch('/api/my-facility', data).then((r) => r.data);

const facilityParams = (facilityId) => (facilityId ? { facility_id: facilityId } : undefined);

export const getFacilityApiKeys = (facilityId) =>
  axios.get('/api/my-facility/api-keys', { params: facilityParams(facilityId) }).then((r) => r.data);

export const createFacilityApiKey = (label, facilityId) =>
  axios.post('/api/my-facility/api-keys', { label, ...(facilityId ? { facility_id: facilityId } : {}) }).then((r) => r.data);

export const revokeFacilityApiKey = (id, facilityId) =>
  axios.delete(`/api/my-facility/api-keys/${id}`, { params: facilityParams(facilityId) }).then((r) => r.data);

export const getPendingLinks = () =>
  axios.get('/api/admin/pending-links').then((r) => r.data);

export const approveLink = (userId) =>
  axios.post(`/api/admin/approve-link/${userId}`).then((r) => r.data);

export const denyLink = (userId) =>
  axios.post(`/api/admin/deny-link/${userId}`).then((r) => r.data);

// Facility search for linking (reuses full search, returns name + location only)
export const searchFacilitiesForLink = (search) =>
  axios.get('/api/facilities/full', { params: { search } }).then((r) => r.data);

// ---------------------------------------------------------------------------
// Bed Alerts (authenticated)
// ---------------------------------------------------------------------------

export const getAlerts = () =>
  axios.get('/api/alerts').then((r) => r.data);

export const createAlert = (data) =>
  axios.post('/api/alerts', data).then((r) => r.data);

export const deleteAlert = (id) =>
  axios.delete(`/api/alerts/${id}`).then((r) => r.data);

// ---------------------------------------------------------------------------
// Notifications (authenticated)
// ---------------------------------------------------------------------------

export const getNotifications = () =>
  axios.get('/api/notifications').then((r) => r.data);

export const markNotificationRead = (id) =>
  axios.patch(`/api/notifications/${id}/read`).then((r) => r.data);

export const markAllNotificationsRead = () =>
  axios.patch('/api/notifications/read-all').then((r) => r.data);

export const deleteNotification = (id) =>
  axios.delete(`/api/notifications/${id}`).then((r) => r.data);

export const changePassword = (new_password) =>
  axios.post('/api/auth/change-password', { new_password }).then((r) => r.data);

// ---------------------------------------------------------------------------
// Housing (Oxford Houses)
// ---------------------------------------------------------------------------

export const getHousing = (params) =>
  axios.get('/api/housing', { params }).then((r) => r.data);

export const getHousingCounties = () =>
  axios.get('/api/housing/counties').then((r) => r.data);

export const uploadHousing = (formData) =>
  axios.post('/api/housing/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((r) => r.data);

// ---------------------------------------------------------------------------
// Settings (update banner)
// ---------------------------------------------------------------------------

export const getBanner = () =>
  axios.get('/api/settings/banner').then((r) => r.data);

export const updateBanner = (text) =>
  axios.patch('/api/settings/banner', { text }).then((r) => r.data);

// ---------------------------------------------------------------------------
// Sober Living (admin CRUD)
// ---------------------------------------------------------------------------

export const getSoberLiving = (params) =>
  axios.get('/api/sober-living', { params }).then((r) => r.data);

export const getSoberLivingCounties = () =>
  axios.get('/api/sober-living/counties').then((r) => r.data);

export const uploadSoberLiving = (formData) =>
  axios.post('/api/sober-living/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((r) => r.data);

export const uploadMoud = (formData) =>
  axios.post('/api/moud/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((r) => r.data);

export const uploadWebsites = (formData) =>
  axios.post('/api/upload/websites', formData).then((r) => r.data);

export const createSoberLiving = (data) =>
  axios.post('/api/sober-living', data).then((r) => r.data);

export const updateSoberLiving = (id, data) =>
  axios.patch(`/api/sober-living/${id}`, data).then((r) => r.data);

export const deleteSoberLiving = (id) =>
  axios.delete(`/api/sober-living/${id}`).then((r) => r.data);

// ---------------------------------------------------------------------------
// Secure messaging — key management
// ---------------------------------------------------------------------------

export const registerPublicKey = (publicKey) =>
  axios.post('/api/keys/register', { public_key: publicKey }).then((r) => r.data);

export const getMyKeyStatus = () =>
  axios.get('/api/keys/status/me').then((r) => r.data);

export const getOrgPublicKey = (userId) =>
  axios.get(`/api/keys/${userId}`).then((r) => r.data);

export const rotatePublicKey = (publicKey) =>
  axios.put('/api/keys/rotate', { public_key: publicKey }).then((r) => r.data);
